import { useEffect, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { Form as AntForm, Collapse } from "antd";
import {
  Button,
  Form,
  FormItem,
  useForm,
  Message,
  Select,
} from "@/frontend/components/common";
import { DatePicker, TimePicker } from "antd";
import { tournamentFormatService, disciplineService, subCategoryService, matchTeamService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type {
  CreateTournamentFormatRequestDTO,
  UpdateTournamentFormatRequestDTO,
  ApiResponse,
  TournamentFormatResponseDTO,
  DisciplineResponseDTO,
  SubCategoryResponseDTO,
  MatchTeamResponseDTO,
} from "@/shared/dto";
import { FaPlus, FaTrash } from "react-icons/fa";

interface TournamentFormatFormValues {
  sport: string;
  subCategoryId: string;
  matches: {
    homeTeamId: string;
    awayTeamId: string;
    date: string | Dayjs;
    time: string | Dayjs;
  }[];
}

interface TournamentFormatFormProps {
  tournamentFormatId?: string;
  initialValues?: Partial<TournamentFormatFormValues>;
  tournamentName?: string;
}

export function TournamentFormatForm({ tournamentFormatId, initialValues, tournamentName }: TournamentFormatFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!tournamentFormatId;

  const [disciplines, setDisciplines] = useState<DisciplineResponseDTO[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryResponseDTO[]>([]);
  const [teams, setTeams] = useState<MatchTeamResponseDTO[]>([]);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState<string | null>(null);
  const [loadingDisciplines, setLoadingDisciplines] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    async function loadDisciplines() {
      setLoadingDisciplines(true);
      try {
        const res = await disciplineService.getPaginate({ page: 1, pageSize: 100 });
        if (res.success && res.data) setDisciplines(res.data.data);
      } finally {
        setLoadingDisciplines(false);
      }
    }
    loadDisciplines();
  }, []);

  async function loadSubCategories(disciplineId: string, selectAfterLoad?: string) {
    setLoadingSubCategories(true);
    setSubCategories([]);
    try {
      const res = await subCategoryService.getPaginate({ page: 1, pageSize: 100, disciplineId });
      if (res.success && res.data) {
        setSubCategories(res.data.data);
        if (selectAfterLoad) {
          form.setFieldValue("subCategoryId", selectAfterLoad);
        }
      }
    } finally {
      setLoadingSubCategories(false);
    }
  }

  async function loadTeams(disciplineId: string) {
    setLoadingTeams(true);
    setTeams([]);
    try {
      const res = await matchTeamService.getPaginate({ page: 1, pageSize: 100, disciplineId });
      if (res.success && res.data) setTeams(res.data.data);
    } finally {
      setLoadingTeams(false);
    }
  }

  function handleDisciplineChange(disciplineName: string) {
    const discipline = disciplines.find((d) => d.name === disciplineName);
    if (!discipline) return;
    setSelectedDisciplineId(discipline.id);
    form.setFieldValue("subCategoryId", undefined);
    form.setFieldValue("matches", []);
    loadSubCategories(discipline.id);
    loadTeams(discipline.id);
  }

  useEffect(() => {
    if (!initialValues || disciplines.length === 0) return;

    const discipline = disciplines.find((d) => d.name === initialValues.sport);
    if (!discipline) return;

    setSelectedDisciplineId(discipline.id);
    loadTeams(discipline.id);

    const matches = (initialValues.matches ?? []).map((m: any) => ({
      ...m,
      date: m.date ? dayjs(m.date) : undefined,
      time: m.time ? dayjs(m.time) : undefined,
    }));

    (async () => {
      setLoadingSubCategories(true);
      setSubCategories([]);
      try {
        const res = await subCategoryService.getPaginate({ page: 1, pageSize: 100, disciplineId: discipline.id });
        if (res.success && res.data) {
          const loaded = res.data.data;
          setSubCategories(loaded);
          const subCatId = initialValues.subCategoryId
            ?? loaded.find((s: SubCategoryResponseDTO) => s.category === tournamentName)?.id;
          form.setFieldsValue({ ...initialValues, subCategoryId: subCatId, matches });
        }
      } finally {
        setLoadingSubCategories(false);
      }
    })();
  }, [initialValues, disciplines]);

  const handleSubmit = async (values: TournamentFormatFormValues) => {
    try {
      const subCategory = subCategories.find((s) => s.id === values.subCategoryId);
      const formMatches = Array.from(values.matches ?? []);
      const originalMatches = initialValues?.matches ?? [];
      const matches = formMatches
        .map((m, idx) => m ?? originalMatches[idx])
        .filter((m): m is TournamentFormatFormValues["matches"][number] =>
          Boolean(m?.homeTeamId && m.awayTeamId && m.date && m.time)
        )
        .map((m, idx) => ({
          homeTeamId: m.homeTeamId,
          awayTeamId: m.awayTeamId,
          date: dayjs.isDayjs(m.date) ? m.date.toISOString() : m.date,
          time: dayjs.isDayjs(m.time) ? m.time.toISOString() : m.time,
          order: idx + 1,
        }));

      // const payload = {
      //   name: subCategory?.category ?? values.sport,
      //   sport: values.sport,
      //   config: { stages: matches },
      // };
      const payload = {
        name: subCategory?.category ?? values.sport,
        sport: values.sport,
        config: { matches, subCategoryId: values.subCategoryId },
      };

      let response: ApiResponse<TournamentFormatResponseDTO>;

      if (isEditMode && tournamentFormatId) {
        response = await tournamentFormatService.update(tournamentFormatId, payload as unknown as UpdateTournamentFormatRequestDTO);
      } else {
        response = await tournamentFormatService.create(payload as unknown as CreateTournamentFormatRequestDTO);
      }

      if (response.success && response.data) {
        Message.success({
          content: isEditMode ? "Evento de torneo actualizado correctamente" : "Evento de torneo creado correctamente",
        });
        navigate("/tournament-formats");
      } else {
        Message.error({ content: response.error ?? (isEditMode ? "Error al actualizar" : "Error al crear") });
      }
    } catch (err) {
      Message.error({ content: err instanceof Error ? err.message : "Error inesperado" });
    }
  };

  const teamOptions = teams.map((t) => ({ label: t.name, value: t.id }));

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical" className="w-full">

      {/* Deporte y Categoría */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <FormItem
          name="sport"
          label="Deporte / Disciplina"
          rules={[{ required: true, message: "El deporte es requerido" }]}
        >
          <Select
            placeholder="Seleccionar deporte"
            loading={loadingDisciplines}
            size="large"
            showSearch
            onChange={handleDisciplineChange}
            options={disciplines.map((d) => ({ label: d.name, value: d.name }))}
          />
        </FormItem>

        <FormItem
          name="subCategoryId"
          label="Categoría"
          rules={[{ required: true, message: "La categoría es requerida" }]}
        >
          <Select
            placeholder="Seleccionar categoría"
            loading={loadingSubCategories}
            size="large"
            options={subCategories.map((s) => ({ label: s.category, value: s.id }))}
          />
        </FormItem>
      </div>

      {/* Partidos */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-1">Partidos</h3>
        <p className="text-sm text-gray-500 mb-4">
          Agrega los partidos del evento. El número se asigna automáticamente según el orden.
        </p>

        <AntForm.List name="matches">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div key={field.key} style={{ marginBottom: 16 }}>
                <Collapse
                  className=""
                  items={[
                    {
                      key: field.key,
                      forceRender: true,
                      label: (
                        <span className="font-medium text-gray-700">
                          Partido #{index + 1}
                        </span>
                      ),
                      extra: (
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<FaTrash />}
                          onClick={(e) => { e.stopPropagation(); remove(field.name); }}
                        >
                          Eliminar
                        </Button>
                      ),
                      children: (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                          <FormItem
                            name={[field.name, "homeTeamId"]}
                            label="Equipo local"
                            rules={[{ required: true, message: "El equipo local es requerido" }]}
                          >
                            <Select
                              placeholder={selectedDisciplineId ? "Seleccionar equipo local" : "Primero selecciona un deporte"}
                              loading={loadingTeams}
                              size="large"
                              showSearch
                              disabled={!selectedDisciplineId}
                              options={teamOptions}
                            />
                          </FormItem>

                          <FormItem
                            name={[field.name, "awayTeamId"]}
                            label="Equipo visitante"
                            rules={[
                              { required: true, message: "El equipo visitante es requerido" },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const homeTeamId = getFieldValue(["matches", field.name, "homeTeamId"]);
                                  if (!value || !homeTeamId || value !== homeTeamId) return Promise.resolve();
                                  return Promise.reject(new Error("El equipo visitante no puede ser igual al local"));
                                },
                              }),
                            ]}
                          >
                            <Select
                              placeholder={selectedDisciplineId ? "Seleccionar equipo visitante" : "Primero selecciona un deporte"}
                              loading={loadingTeams}
                              size="large"
                              showSearch
                              disabled={!selectedDisciplineId}
                              options={teamOptions}
                            />
                          </FormItem>

                          <FormItem
                            name={[field.name, "date"]}
                            label="Fecha"
                            rules={[{ required: true, message: "La fecha es requerida" }]}
                          >
                            <DatePicker className="w-full" size="large" placeholder="Seleccionar fecha" format="DD/MM/YYYY" />
                          </FormItem>

                          <FormItem
                            name={[field.name, "time"]}
                            label="Hora"
                            rules={[{ required: true, message: "La hora es requerida" }]}
                          >
                            <TimePicker className="w-full" size="large" placeholder="Seleccionar hora" format="hh:mm a" use12Hours />
                          </FormItem>
                        </div>
                      ),
                    },
                  ]}
                />
                </div>
              ))}

              <Button
                type="dashed"
                icon={<FaPlus />}
                onClick={() => add({ homeTeamId: undefined, awayTeamId: undefined, date: undefined, time: undefined })}
                className="w-full mb-6"
              >
                Agregar Partido
              </Button>
            </>
          )}
        </AntForm.List>
      </div>

      <div className="flex justify-end gap-4 border-t pt-4">
        <Button type="primary" htmlType="submit" size="large">
          {isEditMode ? "Actualizar Evento" : "Crear Evento"}
        </Button>
        <Button size="large" onClick={() => navigate("/tournament-formats")}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
